import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { History, User, Calendar } from "lucide-react";
import { format } from "date-fns";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { getCurrencySymbol } from "@/lib/currencyUtils";
import { useState } from "react";

interface QuoteRevisionHistoryProps {
  quotationId: string;
  currency: string;
}

export const QuoteRevisionHistory = ({ quotationId, currency }: QuoteRevisionHistoryProps) => {
  const [expandedRevisions, setExpandedRevisions] = useState<Set<string>>(new Set());

  const { data: revisions, isLoading } = useQuery({
    queryKey: ["quote-revisions", quotationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_revisions")
        .select("*, profiles(full_name, email)")
        .eq("quotation_id", quotationId)
        .order("revision_number", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const toggleRevision = (revisionId: string) => {
    setExpandedRevisions(prev => {
      const next = new Set(prev);
      if (next.has(revisionId)) {
        next.delete(revisionId);
      } else {
        next.add(revisionId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Quote Revision History</h3>
        </div>
        <p className="text-sm text-muted-foreground">Loading revisions...</p>
      </Card>
    );
  }

  if (!revisions || revisions.length === 0) {
    return null;
  }

  const currencySymbol = getCurrencySymbol(currency);

  const calculateChanges = (current: any, previous: any) => {
    const changes: string[] = [];

    if (current.total_amount !== previous.total_amount) {
      changes.push(
        `Total changed from ${currencySymbol}${previous.total_amount.toLocaleString()} to ${currencySymbol}${current.total_amount.toLocaleString()}`
      );
    }

    if (current.markup_percentage !== previous.markup_percentage) {
      changes.push(
        `Markup changed from ${previous.markup_percentage}% to ${current.markup_percentage}%`
      );
    }

    if (JSON.stringify(current.itinerary_snapshot) !== JSON.stringify(previous.itinerary_snapshot)) {
      const currentItems = current.itinerary_snapshot as any[];
      const previousItems = previous.itinerary_snapshot as any[];
      
      if (currentItems.length !== previousItems.length) {
        changes.push(
          `Itinerary items changed from ${previousItems.length} to ${currentItems.length} items`
        );
      } else {
        changes.push("Itinerary items were modified");
      }
    }

    if (current.terms_and_conditions !== previous.terms_and_conditions) {
      changes.push("Terms and conditions updated");
    }

    if (current.notes !== previous.notes) {
      changes.push("Notes updated");
    }

    return changes;
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <History className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Quote Revision History</h3>
        <Badge variant="outline" className="ml-auto">
          {revisions.length} {revisions.length === 1 ? "Version" : "Versions"}
        </Badge>
      </div>

      <div className="space-y-3">
        {revisions.map((revision: any, index: number) => {
          const previousRevision = revisions[index + 1];
          const changes = previousRevision ? calculateChanges(revision, previousRevision) : [];
          const isExpanded = expandedRevisions.has(revision.id);

          return (
            <Collapsible key={revision.id} open={isExpanded} onOpenChange={() => toggleRevision(revision.id)}>
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={revision.revision_number === 1 ? "default" : "secondary"}>
                        Version {revision.revision_number}
                        {revision.revision_number === 1 && " (Initial)"}
                      </Badge>
                      {index === 0 && (
                        <Badge variant="outline" className="bg-accent/10">
                          Current
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(revision.created_at), "MMM d, yyyy 'at' h:mm a")}
                      </div>
                      {revision.profiles && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {revision.profiles.full_name || revision.profiles.email}
                        </div>
                      )}
                    </div>

                    {revision.change_summary && (
                      <p className="text-sm text-foreground mt-2">{revision.change_summary}</p>
                    )}
                  </div>

                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2">
                      Details
                      <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </Button>
                  </CollapsibleTrigger>
                </div>

                {changes.length > 0 && (
                  <div className="bg-muted/50 rounded-md p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Changes from previous version:</p>
                    <ul className="space-y-1">
                      {changes.map((change, idx) => (
                        <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                          <span className="text-primary">•</span>
                          {change}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <CollapsibleContent className="space-y-3">
                  <Separator />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Subtotal</p>
                      <p className="text-sm font-medium">{currencySymbol}{revision.subtotal.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Markup</p>
                      <p className="text-sm font-medium">{revision.markup_percentage}% ({currencySymbol}{revision.markup_amount.toLocaleString()})</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Amount</p>
                      <p className="text-sm font-medium text-accent">{currencySymbol}{revision.total_amount.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Valid Until</p>
                      <p className="text-sm font-medium">
                        {revision.valid_until ? format(new Date(revision.valid_until), "MMM d, yyyy") : "Not set"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Itinerary Items ({(revision.itinerary_snapshot as any[]).length})</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(revision.itinerary_snapshot as any[]).map((item: any, idx: number) => (
                        <div key={idx} className="bg-background rounded border p-2 text-sm">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{item.title}</p>
                              {item.description && (
                                <p className="text-xs text-muted-foreground">{item.description}</p>
                              )}
                            </div>
                            <p className="text-sm font-medium">
                              {currencySymbol}{((item.cost || 0) * (item.quantity || 1)).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {revision.notes && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Notes</p>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{revision.notes}</p>
                    </div>
                  )}
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </div>
    </Card>
  );
};
